import React, { useState, useEffect } from 'react';
import httpService from '../api/httpService';
import { useAuth } from '../contexts/AuthContext';
import { SHEET_OPTIONS, getFieldDisplayName } from '../constants/fieldDisplayNames';

function ViewData({ toggleOffcanvas, isOffcanvasVisible, handleBackdropClick }) {
  const { userDetails } = useAuth();
  const userId = userDetails?.userid ?? null;
  const isAdmin = userDetails?.admin === true || userDetails?.admin === 1;
  const today = new Date();
  const year = today.getFullYear();
  const pad = (n) => String(n).padStart(2, '0');
  const defaultStartDate = `${year}-01-01`;
  const defaultEndDate = `${year}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const [search, setSearch] = useState({
    sheet: SHEET_OPTIONS[0]?.value || '',
    orgId: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate
  });
  const [organizations, setOrganizations] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [currentSheet, setCurrentSheet] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const result = await httpService.getData('/Organization/organization');
        if (result?.retObject) setOrganizations(Array.isArray(result.retObject) ? result.retObject : []);
      } catch (e) {
        console.error('Failed to load organizations', e);
      }
    };
    fetchOrganizations();
  }, []);

  // Non-admin: keep Registry fixed to user's org (from localStorage/userDetails)
  useEffect(() => {
    if (!isAdmin && userDetails?.orgId != null) {
      setSearch((prev) => ({ ...prev, orgId: String(userDetails.orgId) }));
    }
  }, [isAdmin, userDetails?.orgId]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!search.sheet) {
      setError('Please select a sheet.');
      setLoading(false);
      return;
    }
    setAppliedSearch({ ...search });
    setLoading(true);
    setError('');
    setHasSearched(true);
    try {
      const params = {
        sheet: search.sheet,
        ...(search.orgId && { orgId: search.orgId }),
        ...(search.startDate && { startDate: search.startDate }),
        ...(search.endDate && { endDate: search.endDate }),
        ...(userId != null && { userId })
      };
      const result = await httpService.getData('/UploadStudyData/Search', params);
      if (result?.isSuccess && result?.retObject) {
        setTableData(Array.isArray(result.retObject.data) ? result.retObject.data : []);
        setCurrentSheet((result.retObject.sheet || search.sheet || '').toLowerCase());
      } else {
        setTableData([]);
        setCurrentSheet(search.sheet);
        setError(result?.message || 'No data returned.');
      }
    } catch (err) {
      console.error('View data error:', err);
      setTableData([]);
      setCurrentSheet(search.sheet);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
      if (isOffcanvasVisible === 'search-filter') toggleOffcanvas('search-filter');
    }
  };

  // Initial load: current year, first sheet
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idKeys = ['fileid', 'patientcharacteristicsid', 'graftselectionid', 'outcomesaclrid', 'patientreportedoutcomeid', 'injurymechanismid', 'concurrentinjuryid'];
  const rawColumns = tableData.length > 0
    ? Object.keys(tableData[0]).filter((k) => !idKeys.includes((k || '').toLowerCase()))
    : [];

  // Order columns: main field immediately followed by its percentage (e.g. Gender Male | Gender Male Percentage)
  const columns = (() => {
    if (rawColumns.length === 0) return [];
    const set = new Set(rawColumns);
    const ordered = [];
    rawColumns.forEach((k) => {
      if (k.endsWith('Percent')) return;
      ordered.push(k);
      const percentKey = k + 'Percent';
      if (set.has(percentKey)) ordered.push(percentKey);
    });
    rawColumns.forEach((k) => {
      if (k.endsWith('Percent') && !ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  })();

  // Human-readable header: title from fieldDisplayNames.js, else format camelCase as "Title Case"
  const getHeaderLabel = (key) => {
    const title = getFieldDisplayName(key, currentSheet);
    if (title) return title;
    const str = String(key);
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .replace(/Percent$/i, ' Percentage')
      .trim();
  };

  const appliedSheet = appliedSearch?.sheet || '';
  const sheetLabel = appliedSheet
    ? (SHEET_OPTIONS.find((o) => o.value === appliedSheet)?.label || appliedSheet)
    : '–';
  const appliedOrgId = appliedSearch?.orgId;
  const org = appliedOrgId ? organizations.find((o) => String(o.orgid) === String(appliedOrgId)) : null;
  const orgLabel = org
    ? (org.orgname || org.orgName || org.name || appliedOrgId)
    : (appliedOrgId ? String(appliedOrgId) : 'All');

  const appliedFilters = hasSearched && appliedSearch ? (
    <div className="view-data-filter-summary d-flex flex-wrap align-items-center gap-3 small text-muted mb-2 py-2 px-2 bg-light rounded">
      <span><strong>Sheet:</strong> {sheetLabel}</span>
      {isAdmin && <span><strong>Registry:</strong> {orgLabel}</span>}
      {appliedSearch.startDate && <span><strong>Start Date:</strong> {appliedSearch.startDate}</span>}
      {appliedSearch.endDate && <span><strong>End Date:</strong> {appliedSearch.endDate}</span>}
    </div>
  ) : null;

  return (
    <div className="app-content p-4">
      <div className="container-fluid mt-4">
        <div className="card custom-card card-min-height">
          <div className="card-header justify-content-between align-items-center position-relative">
            <div className="card-title">
              {hasSearched && tableData.length > 0 && sheetLabel !== '–'
                ? `View Data – ${sheetLabel}`
                : 'View Data'}
            </div>
            <div className="custom_btns">
              <div className="search-bar">
               {/*  <i className="fas fa-sm fa-search search-icon me-2" /> */}
                {/* <span className="text-muted small align-middle me-2">Filters</span> */}
                <i
                  className="fas fa-sliders-h fa-sm filter-icon me-2"
                  style={{ cursor: 'pointer', color: '#fff' }}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleOffcanvas('search-filter');
                  }}
                />
              </div>
            </div>

            {isOffcanvasVisible && isOffcanvasVisible === 'search-filter' && (
              <>
                <div
                  id="search-filter-backdrop"
                  className="backdrop"
                  onClick={handleBackdropClick}
                  style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                  aria-hidden="true"
                />
                <div
                  className="view-data-filter-panel position-absolute bg-white border rounded shadow p-3"
                  style={{ width: '420px', right: 0, top: '100%', marginTop: '8px', zIndex: 1050 }}
                >
                <form onSubmit={handleSearch} className="view-data-filter-form">
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label htmlFor="view-sheet" className="form-label filter-label small mb-2">Sheet</label>
                      <select
                        id="view-sheet"
                        name="sheet"
                        className="form-select form-select-sm"
                        value={search.sheet}
                        onChange={handleFilterChange}
                      >
                        <option value="">Select sheet</option>
                        {SHEET_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label htmlFor="view-org" className="form-label filter-label small mb-2">Registry</label>
                      <select
                        id="view-org"
                        name="orgId"
                        className="form-select form-select-sm mb-3"
                        value={isAdmin ? search.orgId : String(userDetails?.orgId ?? '')}
                        onChange={handleFilterChange}
                        disabled={!isAdmin}
                      >
                        <option value="">All</option>
                        {organizations.map((org) => (
                          <option key={org.orgid} value={org.orgid}>{org.orgname || org.orgName || org.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label htmlFor="view-start" className="form-label filter-label small mb-2">Start Date</label>
                      <input
                        type="date"
                        id="view-start"
                        name="startDate"
                        className="form-control form-control-sm mb-3"
                        value={search.startDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="view-end" className="form-label filter-label small mb-2">End Date</label>
                      <input
                        type="date"
                        id="view-end"
                        name="endDate"
                        className="form-control form-control-sm mb-3"
                        value={search.endDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <button type="submit" className="btn blue-btn btn-sm" disabled={loading}>
                      {loading ? 'Loading...' : 'Search'}
                    </button>
                  </div>
                </form>
                </div>
              </>
            )}
          </div>

          <div className="card-body">
            {error && <div className="alert alert-warning mb-3">{error}</div>}
            {hasSearched && appliedFilters}
            {loading ? (
              <div className="d-flex justify-content-center align-items-center loader-container">
                <button className="btn btn-outline-secondary mt-3" type="button" disabled>
                  <span className="spinner-border spinner-border-sm align-middle me-2" role="status" aria-hidden="true" />
                  Loading...
                </button>
              </div>
            ) : (
              hasSearched && (
                <div className="table-responsive view-data-table-wrapper mt-3">
                  <table id="dataTable" className="table table-hover table-bordered view-data-table">
                    <thead>
                      <tr>
                        {columns.map((key) => (
                          <th key={key}>{getHeaderLabel(key)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.length === 0 ? (
                        <tr><td colSpan={columns.length || 1} className="text-center text-muted">No records found.</td></tr>
                      ) : (
                        tableData.map((row, rIdx) => (
                          <tr key={rIdx}>
                            {columns.map((key) => (
                              <td key={key}>{row[key] != null && row[key] !== '' ? String(row[key]) : '–'}</td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}
            {!hasSearched && !loading && (
              <p className="text-muted mt-3">Use the filter icon to select a sheet and search.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewData;
